/** Full-page create host for a person. Existing profiles use PersonProfileSection. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch, type Resolver } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import { showApiErrorToast } from "@/lib/api-error-toast";
import type { User } from "@/api/people";
import { PersonFormFields } from "@/components/people/PersonFormFields";
import { collectPersonValidationIssues } from "@/components/people/personValidationIssues";
import {
  buildPersonPatch,
  getPersonFormDefaults,
  personCreateSchema,
  personEditSchema,
  type PersonCreateFormValues,
  type PersonEditFormValues,
  type PersonFormValues,
} from "@/components/people/personFormModel";
import {
  clearPersonCreateDraft,
  loadPersonCreateDraft,
  savePersonCreateDraft,
} from "@/components/people/personCreateDraft";
import { Button } from "@/components/ui/button";
import { Surface } from "@/components/ui/surface";
import {
  apiErrorFormIssue,
  FormErrorSummary,
  focusValidationControl,
  type FormValidationIssue,
} from "@/components/ui/form-validation";
import { useCreatePerson, usePatchPerson } from "@/hooks/usePeople";
import { usePositions } from "@/hooks/usePositions";
import { useCalendars } from "@/hooks/useTemplates";
import { useUnits } from "@/hooks/useUnits";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";

export interface PersonFormProps {
  mode: "create" | "edit";
  onOpenChange: (open: boolean) => void;
  onCancel?: () => void;
  user?: User;
  onCreated?: (tempPassword: string | null, user: User) => void;
  currentUserId?: number | null;
  activeAdminsCount?: number;
  allowedRoles?: string[];
  lockedUnitId?: number | null;
  initialUnitId?: number | null;
  actorId?: number | null;
  canManageProjectManager?: boolean;
}

export function PersonForm({
  mode,
  onOpenChange,
  onCancel,
  user,
  onCreated,
  currentUserId = null,
  activeAdminsCount = 0,
  allowedRoles,
  lockedUnitId = null,
  initialUnitId = null,
  actorId = null,
  canManageProjectManager = false,
}: PersonFormProps): React.ReactElement {
  const { t } = useTranslation();
  const isCreate = mode === "create";
  const createMut = useCreatePerson();
  const patchMut = usePatchPerson();
  const unitsQuery = useUnits();
  const positionsQuery = usePositions(true);
  const calendarsQuery = useCalendars();
  const defaults = useMemo(
    () =>
      getPersonFormDefaults({
        mode,
        user,
        allowedRoles,
        lockedUnitId,
        initialUnitId,
      }),
    [allowedRoles, initialUnitId, lockedUnitId, mode, user],
  );
  const form = useForm<PersonFormValues>({
    resolver: zodResolver(
      isCreate ? personCreateSchema : personEditSchema,
    ) as Resolver<PersonFormValues>,
    defaultValues: defaults,
    mode: "onChange",
  });
  useReleaseDirtyState(form.formState.isDirty);
  const draftValues = useWatch({ control: form.control });
  const draftHydrated = useRef(false);
  const creationDone = useRef(false);
  const [serverError, setServerError] = useState<FormValidationIssue | null>(null);

  const validationIssues = collectPersonValidationIssues(
    form.formState.errors as Record<string, { message?: unknown } | undefined>,
    form.getValues("group"),
    t,
  );
  const issues: FormValidationIssue[] = serverError
    ? [...validationIssues, serverError]
    : validationIssues;

  useEffect(() => {
    form.reset(defaults);
  }, [defaults, form]);

  useEffect(() => {
    if (!isCreate || actorId == null || draftHydrated.current) return;
    if (unitsQuery.isLoading || positionsQuery.isLoading || calendarsQuery.isLoading) return;
    draftHydrated.current = true;
    const draft = loadPersonCreateDraft(actorId, {
      unitIds: new Set(
        (unitsQuery.data ?? []).flatMap((item) => (item.id == null ? [] : [item.id])),
      ),
      positionIds: new Set(
        (positionsQuery.data ?? []).flatMap((item) => (item.id == null ? [] : [item.id])),
      ),
      calendarIds: new Set(
        (calendarsQuery.data ?? []).flatMap((item) => (item.id == null ? [] : [item.id])),
      ),
    });
    if (draft) {
      form.reset({
        ...defaults,
        ...draft,
        ...(initialUnitId != null ? { unit_id: initialUnitId } : {}),
      } as PersonFormValues);
    }
  }, [
    actorId,
    calendarsQuery.data,
    calendarsQuery.isLoading,
    defaults,
    form,
    initialUnitId,
    isCreate,
    positionsQuery.data,
    positionsQuery.isLoading,
    unitsQuery.data,
    unitsQuery.isLoading,
  ]);

  useEffect(() => {
    if (!isCreate || actorId == null || creationDone.current || !draftHydrated.current) return;
    const timer = setTimeout(() => savePersonCreateDraft(actorId, draftValues), 500);
    return () => clearTimeout(timer);
  }, [actorId, draftValues, isCreate]);

  async function onSubmit(rawValues: PersonFormValues): Promise<void> {
    setServerError(null);
    try {
      if (isCreate) {
        const values = rawValues as PersonCreateFormValues;
        if (values.group === "worker") {
          const response = await createMut.mutateAsync({
            last_name: values.last_name.trim(),
            first_name: values.first_name.trim(),
            patronymic: values.patronymic.trim(),
            role: "worker",
            is_project_manager: false,
            unit_id: values.unit_id,
            calendar_id: values.calendar_id,
            tab_number: values.tab_number,
            pin: values.pin,
            position_id: values.position_id,
            hire_date: values.hire_date,
            termination_date: values.termination_date,
          });
          toast.success(t("admin.users.toast.workerCreated"));
          creationDone.current = true;
          if (actorId != null) clearPersonCreateDraft(actorId);
          onOpenChange(false);
          onCreated?.(null, response.user);
          return;
        }
        const response = await createMut.mutateAsync({
          last_name: values.last_name.trim(),
          first_name: values.first_name.trim(),
          patronymic: values.patronymic.trim(),
          role: values.role,
          web_email: values.web_email,
          unit_id: values.unit_id,
          calendar_id: values.calendar_id,
          tab_number: values.tab_number || null,
          position_id: values.position_id,
          hire_date: values.hire_date,
          termination_date: values.termination_date,
          is_project_manager: values.is_project_manager,
        });
        toast.success(t("admin.users.toast.created"));
        creationDone.current = true;
        if (actorId != null) clearPersonCreateDraft(actorId);
        onOpenChange(false);
        onCreated?.(response.temporary_password, response.user);
        return;
      }

      if (user?.id != null) {
        await patchMut.mutateAsync({
          id: user.id,
          body: buildPersonPatch(rawValues as PersonEditFormValues, form.formState.dirtyFields),
        });
        toast.success(t("admin.users.toast.saved"));
        onOpenChange(false);
      }
    } catch (error) {
      setServerError(
        apiErrorFormIssue(error, {
          id: "person-form-server-error",
          fallbackMessage: t("common.apiError.fallback"),
        }),
      );
      showApiErrorToast(error, { fallbackMessage: t("common.apiError.fallback") });
    }
  }

  function handleFormSubmit(event: React.FormEvent<HTMLFormElement>): void {
    void form.handleSubmit(onSubmit, (invalidErrors) => {
      const first = collectPersonValidationIssues(
        invalidErrors as Record<string, { message?: unknown } | undefined>,
        form.getValues("group"),
        t,
      )[0]?.controlId;
      if (first) requestAnimationFrame(() => focusValidationControl(first));
    })(event);
  }

  return (
    <Surface className="mt-6 overflow-hidden">
      <form onSubmit={handleFormSubmit} data-testid="person-form">
        <PersonFormFields
          mode={mode}
          form={form}
          user={user}
          currentUserId={currentUserId}
          activeAdminsCount={activeAdminsCount}
          allowedRoles={allowedRoles}
          lockedUnitId={lockedUnitId}
          initialUnitId={initialUnitId}
          canManageProjectManager={canManageProjectManager}
          validationIssues={validationIssues}
        />
        <div className="px-4 pb-3">
          <FormErrorSummary
            issues={issues}
            countLabel={(count) => t("validation.fieldErrorCount", { count })}
            data-testid="person-form-error-summary"
          />
        </div>
        <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-[var(--rule-line)] bg-popover px-4 py-3">
          {isCreate && actorId != null && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                clearPersonCreateDraft(actorId);
                form.reset(defaults);
              }}
              data-testid="person-form-clear"
            >
              {t("common.clear")}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => (onCancel ? onCancel() : onOpenChange(false))}
            data-testid="user-form-cancel"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            data-testid="user-form-submit"
          >
            {isCreate ? t("admin.users.create.submit") : t("admin.users.edit.submit")}
          </Button>
        </footer>
      </form>
    </Surface>
  );
}
